import type { CampaignCandidateResponse } from 'clients/CampaignsClient/CampaignsClient.types';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { Candidate } from 'model/Candidate/Candidate';
import { useContext } from 'react';
import { CANDIDATE_STATUS } from 'services/candidates';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { useGetAllAtsConnectionsQuery } from 'shared/legacy/hooks/useGetAllAtsConnectionsQuery';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { useCandidateStore } from 'stores/candidate/candidate.store';
import { QueryKey } from 'types/query';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';

export const useMatch = (params: WrappedHookParams) => {
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);
  const pageContext = usePageContext();
  const setSelectedCandidate = useCandidateStore((state) => state.setSelectedCandidate);
  const selectedCandidate = useCandidateStore((state) => state.selectedCandidate);
  const campaign = useCampaignStore((s) => s.campaign);
  const { data: atsConnections } = useGetAllAtsConnectionsQuery();

  return useCandidateAction<MatchHookCallbackParams, CampaignCandidateResponse>(
    async ({ candidate, campaignId }) => {
      const { data: newCandidate } = await campaignsClient.createOrUpdateCampaignCandidate({
        campaignId: campaignId ?? candidate.campaignId,
        esPersonId: candidate.es_person_id,
        status: CANDIDATE_STATUS.MATCHED,
        is_unlocked: candidate.isUnlocked,
      });

      return newCandidate;
    },
    {
      onError: () => {
        return async ({ candidateOrchestrator: co, data: error }) => {
          if (isAxiosError(error) && error.response?.status === 409) {
            createSnackbar(dictionary.candidateAlreadyLinkedToAnotherMatch, {
              variant: 'danger',
            });

            co.campaign.decreaseStatOf.currentJobBoard();
            co.campaign.decreaseStatOf.currentList();
            co.campaign.remove.fromCurrentList();
          }
          throw error;
        };
      },
      onSuccess: ({ fcParams: { candidate, campaignId }, gtm }) => {
        gtm.matchCandidate(campaignId ?? candidate.campaignId, candidate.es_person_id, pageContext);

        if (campaign?.connected_ats && atsConnections?.activeATS) {
          const atsName = atsConnections?.activeATS?.name || dictionary.yourAts;
          const candidateFirstName = candidate.firstName || dictionary.theCandidate;

          createSnackbar(dictionary.candidateSentToAts(candidateFirstName, atsName), {
            variant: 'success',
            title: dictionary.sentToAts,
          });
        } else {
          createSnackbar(dictionary.candidateIsMovedTo(candidate.firstName, dictionary.matched), {
            variant: 'success',
          });
        }

        queryClient.invalidateQueries(
          [QueryKey.useCandidateActivityLogQuery, { candidateEsPersonId: candidate.es_person_id }],
          { exact: false },
        );

        return async ({ candidateOrchestrator: co, pageContext, data: newCandidate }) => {
          if (pageContext === PageContext.Campaign) {
            if (candidate.status === CANDIDATE_STATUS.NEW) {
              co.campaign.decreaseStatOf.currentJobBoard();
            }

            co.campaign.remove.fromCurrentList();
            co.campaign.reset.matchList();
            co.campaign.decreaseStatOf.currentList();
            co.campaign.increaseStatOf.matchList();
          } else if (pageContext === PageContext.RatedCandidates) {
            co.ratedCandidates.remove.fromCurrentList();
            co.ratedCandidates.reset.matchList();
            co.ratedCandidates.decreaseStatOf.currentList();
            co.ratedCandidates.increaseStatOf.matchList();
          } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
            co.share.moveCandidateTo.matchList();
          } else if (pageContext === PageContext.CentralInbox) {
            queryClient.invalidateQueries([
              QueryKey.useCandidateHistoryQuery,
              { candidateEsPersonId: candidate?.es_person_id, status: candidate?.status },
            ]);

            const _campaignId = campaignId ?? newCandidate.campaign?.id ?? selectedCandidate?.rawCandidate.campaign?.id;
            if (!_campaignId) {
              throw Error('Campaign ID is required to do a match');
            }

            setSelectedCandidate(_campaignId, new Candidate(_campaignId, newCandidate).rawCandidate);
          }
        };
      },
    },
  )(params);
};

type MatchHookCallbackParams = {
  campaignId?: number;
};
